import React from 'react';
import {
    ActivityType,
    GoalActivityFilterDto,
    GoalFitnessMetricType,
    GoalRuleType,
    WorkoutCategory,
    WorkoutTypeV2
} from '@api/FitnessActivityClient';
import { Badge } from '@components/ui/badge';
import {
    ActivityFilterFormValue,
    ActivityFiltersEditor,
    createEmptyActivityFilter,
    formatEnumLabel,
    normalizeActivityFilters
} from '@core/activity-filters/editor';

export interface GoalActivityFilterFormValue extends ActivityFilterFormValue {
    activityType?: ActivityType;
    workoutCategory?: WorkoutCategory;
    workoutType?: WorkoutTypeV2;
    fitnessMetricType?: GoalFitnessMetricType;
}

interface GoalActivityFilterSource {
    activityFilters?: GoalActivityFilterDto[];
    activityType?: ActivityType;
    workoutCategory?: WorkoutCategory;
    workoutType?: WorkoutTypeV2;
}

const ACTIVITY_FILTER_RULE_TYPES = new Set<GoalRuleType>([
    GoalRuleType.ACTIVITY_POINTS,
    GoalRuleType.ACTIVITY_RAW_VALUE,
    GoalRuleType.ACTIVITY_COUNT
]);

const WORKOUT_CATEGORY_OPTIONS = Object.values(WorkoutCategory);
const WORKOUT_TYPE_OPTIONS = Object.values(WorkoutTypeV2);
const ACTIVITY_TYPE_OPTIONS = Object.values(ActivityType);
const FITNESS_METRIC_TYPE_OPTIONS = Object.values(GoalFitnessMetricType);

export const createEmptyGoalActivityFilter = (): GoalActivityFilterFormValue =>
    createEmptyActivityFilter() as GoalActivityFilterFormValue;

export const supportsGoalActivityFilters = (ruleType?: GoalRuleType): boolean =>
    ruleType !== undefined && ACTIVITY_FILTER_RULE_TYPES.has(ruleType);

export const goalActivityFiltersFromSource = (
    source: GoalActivityFilterSource
): GoalActivityFilterFormValue[] => {
    if (source.activityFilters && source.activityFilters.length > 0) {
        return source.activityFilters.map((filter) => ({
            activityType: filter.activityType,
            workoutCategory: filter.workoutCategory,
            workoutType: filter.workoutType,
            hasArrivalLocation: filter.hasArrivalLocation,
            minRawValue: filter.minRawValue,
            fitnessMetricType: filter.fitnessMetricType
        }));
    }

    if (source.activityType || source.workoutCategory || source.workoutType) {
        return [
            {
                activityType: source.activityType,
                workoutCategory: source.workoutCategory,
                workoutType: source.workoutType
            }
        ];
    }

    return [];
};

export const normalizeGoalActivityFilters = (
    filters: GoalActivityFilterFormValue[]
): GoalActivityFilterDto[] =>
    normalizeActivityFilters(
        filters,
        ActivityType.WORKOUT
    ) as GoalActivityFilterDto[];

export const buildGoalActivityFilterPayload = (
    filters: GoalActivityFilterFormValue[]
): GoalActivityFilterSource => {
    const normalizedFilters = normalizeGoalActivityFilters(filters);

    if (normalizedFilters.length === 0) {
        return {
            activityType: undefined,
            workoutCategory: undefined,
            workoutType: undefined,
            activityFilters: undefined
        };
    }

    if (normalizedFilters.length === 1) {
        return {
            activityType: normalizedFilters[0].activityType,
            workoutCategory: normalizedFilters[0].workoutCategory,
            workoutType: normalizedFilters[0].workoutType,
            activityFilters: normalizedFilters
        };
    }

    return {
        activityType: undefined,
        workoutCategory: undefined,
        workoutType: undefined,
        activityFilters: normalizedFilters
    };
};

export const formatGoalActivityFilterLabel = (
    filter: GoalActivityFilterFormValue
): string => {
    const parts = [
        filter.activityType,
        filter.workoutCategory,
        filter.workoutType
    ]
        .filter(Boolean)
        .map((value) => formatEnumLabel(value!));

    if (filter.hasArrivalLocation) {
        parts.push('At arrival location');
    }

    if (filter.minRawValue) {
        parts.push(
            `Min ${filter.minRawValue}${
                filter.fitnessMetricType
                    ? ` ${formatEnumLabel(filter.fitnessMetricType)}`
                    : ''
            }`
        );
    } else if (filter.fitnessMetricType) {
        parts.push(formatEnumLabel(filter.fitnessMetricType));
    }

    return parts.length > 0 ? parts.join(' / ') : 'All activities';
};

interface GoalActivityFiltersEditorProps {
    value: GoalActivityFilterFormValue[];
    onChange: (value: GoalActivityFilterFormValue[]) => void;
    title?: string;
    helperText?: string;
}

export const GoalActivityFiltersEditor: React.FC<
    GoalActivityFiltersEditorProps
> = ({ value, onChange, title, helperText }) => (
    <ActivityFiltersEditor
        value={value}
        onChange={(filters) =>
            onChange(filters as GoalActivityFilterFormValue[])
        }
        activityTypeOptions={ACTIVITY_TYPE_OPTIONS}
        workoutCategoryOptions={WORKOUT_CATEGORY_OPTIONS}
        workoutTypeOptions={WORKOUT_TYPE_OPTIONS}
        fitnessMetricOptions={FITNESS_METRIC_TYPE_OPTIONS}
        workoutActivityType={ActivityType.WORKOUT}
        title={title}
        helperText={helperText}
    />
);

interface GoalActivityFiltersSummaryProps extends GoalActivityFilterSource {
    label?: string;
    emptyStateText?: string;
}

export const GoalActivityFiltersSummary: React.FC<
    GoalActivityFiltersSummaryProps
> = ({
    activityFilters,
    activityType,
    workoutCategory,
    workoutType,
    label = 'Counted activity sets',
    emptyStateText
}) => {
    const filters = goalActivityFiltersFromSource({
        activityFilters,
        activityType,
        workoutCategory,
        workoutType
    });

    if (filters.length === 0) {
        if (!emptyStateText) {
            return null;
        }

        return (
            <p className="mb-4 text-sm text-muted-foreground">
                <strong>{label}:</strong> {emptyStateText}
            </p>
        );
    }

    return (
        <div className="mb-4">
            <p className="mb-2 text-sm text-muted-foreground">
                <strong>{label}:</strong>
            </p>
            <div className="flex flex-wrap gap-2">
                {filters.map((filter, index) => (
                    <Badge
                        key={`${formatGoalActivityFilterLabel(filter)}-${index}`}
                        variant="outline"
                    >
                        {formatGoalActivityFilterLabel(filter)}
                    </Badge>
                ))}
            </div>
        </div>
    );
};
